import { Link } from 'react-router-dom';

const footerLinks = {
  Services: ['Website Design', 'AI Integration', 'Local SEO', 'GEO / AEO', 'Competitor Analysis'],
  Company: ['About Us', 'Our Work', 'Results', 'Contact'],
  Industries: ['Tourism', 'Hospitality', 'Dive & Reef', 'Ferry Services', 'Adventure Charters'],
};

export default function Footer() {
  const handleNav = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <footer className="relative bg-ds-bg-2 border-t border-ds-border overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-ds-red/30 to-transparent" />
      <div className="absolute bottom-0 right-0 w-96 h-64 bg-ds-red/5 blur-[100px] rounded-full pointer-events-none" />

      {/* Main footer */}
      <div className="max-w-7xl mx-auto px-6 pt-16 pb-8">
        <div className="grid lg:grid-cols-6 gap-12 mb-12">
          {/* Brand col */}
          <div className="lg:col-span-2">
            <img
              src="https://storage.readdy-site.link/project_files/cb16f62b-b978-4a18-b0d4-78b46c2aa554/9ae0cf67-cf30-4444-9d14-012a7830e862_Darkside-Digital-logo-design.png?v=7acdaa383d113350b9c301867b0d13f1"
              alt="Darkside Digital"
              className="h-10 w-auto object-contain mb-4"
              loading="lazy"
              decoding="async"
            />
            <p className="font-heading font-700 text-white/80 text-xs tracking-[0.35em] uppercase mb-4">
              We Build. We Dominate. We Deliver.
            </p>
            <p className="font-body text-white/40 text-sm leading-relaxed max-w-xs">
              The AI-powered digital agency giving local businesses an unfair advantage over their competition.
            </p>

            {/* Phone */}
            <a
              href="tel:1300029054"
              className="flex items-center gap-2.5 mt-5 group"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-phone-line text-ds-red text-sm" />
              </div>
              <span className="font-body text-white/50 group-hover:text-white text-sm transition-colors duration-200">
                1300 029 054
              </span>
            </a>

            {/* Email */}
            <a
              href="mailto:hello@darksidedigital.com.au"
              className="flex items-center gap-2.5 mt-3 group"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-mail-line text-ds-red text-sm" />
              </div>
              <span className="font-body text-white/50 group-hover:text-white text-sm transition-colors duration-200">
                hello@darksidedigital.com.au
              </span>
            </a>

            {/* Social icons */}
            <div className="flex items-center gap-3 mt-6">
              {[
                { icon: 'ri-instagram-line', href: '#' },
                { icon: 'ri-facebook-box-line', href: '#' },
                { icon: 'ri-linkedin-box-line', href: '#' },
                { icon: 'ri-twitter-x-line', href: '#' },
              ].map((social) => (
                <a
                  key={social.icon}
                  href={social.href}
                  rel="nofollow"
                  className="w-9 h-9 flex items-center justify-center border border-ds-border text-white/40 hover:text-ds-red hover:border-ds-red/50 transition-all duration-200 cursor-pointer"
                >
                  <i className={`${social.icon} text-base`} />
                </a>
              ))}
            </div>
          </div>

          {/* Links */}
          {Object.entries(footerLinks).map(([category, links]) => (
            <div key={category}>
              <h4 className="font-heading font-700 text-white text-xs tracking-widest uppercase mb-4">
                <a href={`#${category.toLowerCase()}`}>{category}</a>
              </h4>
              <ul className="space-y-2.5">
                {links.map((link) => {
                  if (category === 'Industries' && link === 'Tourism') {
                    return (
                      <li key={link}>
                        <Link
                          to="/tourism-website-design"
                          className="font-body text-ds-red hover:text-white text-sm transition-colors duration-200 cursor-pointer"
                        >
                          {link} Websites
                        </Link>
                      </li>
                    );
                  }
                  return (
                    <li key={link}>
                      <button
                        onClick={() => handleNav('#services')}
                        className="font-body text-white/40 hover:text-white text-sm transition-colors duration-200 cursor-pointer text-left"
                      >
                        {link}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}

          {/* Service Areas quick links */}
          <div>
            <h4 className="font-heading font-700 text-white text-xs tracking-widest uppercase mb-4">
              <a href="/service-areas">Service Areas</a>
            </h4>
            <ul className="space-y-2.5">
              {['Mission Beach', 'Cassowary Coast', 'Cairns', 'Townsville', 'All of Australia'].map((area) => (
                <li key={area}>
                  <a
                    href={`/service-areas#${area.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`}
                    className="font-body text-white/40 hover:text-white text-sm transition-colors duration-200 cursor-pointer"
                  >
                    {area}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Divider */}
        <div className="h-px bg-ds-border mb-8" />

        {/* ── Contact + Map strip ── */}
        <div className="grid lg:grid-cols-2 gap-8 mb-8">
          {/* Left: contact details */}
          <div>
            <p className="font-heading font-700 text-white text-xs tracking-[0.3em] uppercase mb-5">
              Get In Touch
            </p>

            <div className="space-y-3">
              {/* Business name */}
              <div className="flex items-start gap-3">
                <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                  <i className="ri-building-line text-ds-red text-sm" />
                </div>
                <span className="font-body text-white/60 text-sm">Darkside Digital</span>
              </div>

              {/* Address */}
              <div className="flex items-start gap-3">
                <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                  <i className="ri-map-pin-line text-ds-red text-sm" />
                </div>
                <span className="font-body text-white/60 text-sm leading-relaxed">
                  Mission Beach, Queensland 4852, Australia
                </span>
              </div>

              {/* Phone */}
              <div className="flex items-center gap-3">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-phone-line text-ds-red text-sm" />
                </div>
                <a
                  href="tel:1300029054"
                  className="font-body text-white/60 hover:text-white text-sm transition-colors duration-200"
                >
                  1300 029 054
                </a>
              </div>

              {/* Email */}
              <div className="flex items-center gap-3">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-mail-line text-ds-red text-sm" />
                </div>
                <a
                  href="mailto:hello@darksidedigital.com.au"
                  className="font-body text-white/60 hover:text-white text-sm transition-colors duration-200"
                >
                  hello@darksidedigital.com.au
                </a>
              </div>

              {/* Service area */}
              <div className="flex items-start gap-3 pt-1">
                <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                  <i className="ri-global-line text-ds-red text-sm" />
                </div>
                <p className="font-body text-white/40 text-xs leading-relaxed max-w-sm">
                  Serving businesses across Mission Beach, Cassowary Coast, Far North Queensland, and all of Australia remotely.
                </p>
              </div>
            </div>
          </div>

          {/* Right: Google Maps embed */}
          <div className="flex flex-col justify-start">
            <p className="font-heading font-700 text-white text-xs tracking-[0.3em] uppercase mb-5">
              Find Us
            </p>
            <div className="rounded-lg overflow-hidden border border-ds-border/60" style={{ height: '200px' }}>
              <iframe
                title="Mission Beach QLD — Darkside Digital"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d29998.0!2d146.10416!3d-17.86128!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6980c4a54567b1d3%3A0xf02a35bd720aec0!2sMission%20Beach%20QLD%204852%2C%20Australia!5e0!3m2!1sen!2sau!4v1710000000000!5m2!1sen!2sau"
                width="100%"
                height="200"
                style={{ border: 0, filter: 'grayscale(60%) invert(10%) brightness(0.85)' }}
                allowFullScreen={false}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>

        {/* Second divider before copyright */}
        <div className="h-px bg-ds-border mb-8" />

        {/* Bottom bar */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="font-body text-white/30 text-xs">
            &copy; 2015 Darkside Digital. All rights reserved.
          </p>
          <p className="font-body text-white/20 text-xs">
            Built to dominate. Powered by AI.
          </p>
          <div className="flex items-center gap-4">
            <button className="font-body text-white/30 hover:text-white text-xs transition-colors duration-200 cursor-pointer whitespace-nowrap">
              Privacy Policy
            </button>
            <span className="text-white/20 text-xs">•</span>
            <button className="font-body text-white/30 hover:text-white text-xs transition-colors duration-200 cursor-pointer whitespace-nowrap">
              Terms of Service
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}
